import { useEffect } from 'react'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import Footer from '../components/Footer'
import CommunityRequests from '../components/CommunityRequests'
import RequestFeatureModal, { openRequestFeatureModal } from '../components/RequestFeatureModal'

interface Phase {
  period: string
  title: string
  tone: 'now' | 'next' | 'soon' | 'later'
  items: string[]
}

const PHASES: Phase[] = [
  {
    period: 'now',
    title: 'deploy agent',
    tone: 'now',
    items: ['plugin skill', 'detects framework + env', 'one-command deploy', 'returns live URL'],
  },
  {
    period: 'next',
    title: 'runtime agent',
    tone: 'next',
    items: ['understands service graph', 'scales services safely', 'optimizes infra cost', 'keeps production healthy'],
  },
  {
    period: 'soon',
    title: 'oncall agent',
    tone: 'soon',
    items: ['watches deploy failures and notifies your agent', 'reads logs + metrics', 'explains root cause', 'fixes and retries'],
  },
  {
    period: 'later',
    title: 'security agent',
    tone: 'later',
    items: ['detects leaked secrets', 'scans vulnerable deps', 'patches unsafe configs', 'blocks risky deploys'],
  },
]

const ROADMAP_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'TokenDance roadmap',
  description:
    "TokenDance is building the agent layer that ships, watches, fixes, and scales your app.",
  itemListOrder: 'https://schema.org/ItemListOrderAscending',
  itemListElement: PHASES.map((phase, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: `${phase.period} — ${phase.title}`,
    description: phase.items.join(' '),
  })),
}

export default function Roadmap() {
  useEffect(() => {
    document.body.classList.add('v2-roadmap-page')
    return () => document.body.classList.remove('v2-roadmap-page')
  }, [])

  return (
    <>
      <SEO
        title="Roadmap — TokenDance"
        description="We are building the agent layer that ships, watches, fixes, and scales your app."
        path="/roadmap"
        jsonLd={ROADMAP_JSONLD}
      />

      <div className="od-home od-roadmap-shell">
        <span className="od-corner od-corner-tl" aria-hidden="true" />
        <span className="od-corner od-corner-tr" aria-hidden="true" />
        <span className="od-corner od-corner-bl" aria-hidden="true" />
        <span className="od-corner od-corner-br" aria-hidden="true" />

        <SiteNav />

        <main className="od-roadmap-main">
          <section className="od-roadmap-hero" aria-labelledby="roadmap-title">
            <h1 id="roadmap-title">
              Built in public.
              <br />
              Shipping fast.
            </h1>
            <p className="od-roadmap-copy">
              <span>We're building the agent layer that ships, watches, fixes, and scales your app.</span>
            </p>
          </section>

          <div className="od-roadmap-track" aria-hidden="true">
            {PHASES.map((phase) => (
              <span key={phase.period} className={`od-roadmap-node od-roadmap-node-${phase.tone}`} />
            ))}
          </div>

          <ol className="od-roadmap-grid" aria-label="TokenDance roadmap phases">
            {PHASES.map((phase) => (
              <li key={phase.period} className={`od-roadmap-card od-roadmap-card-${phase.tone}`}>
                <div className="od-roadmap-card-head">
                  <span>{phase.period}</span>
                  <i aria-hidden="true" />
                </div>
                <h2>{phase.title}</h2>
                <ul>
                  {phase.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>

          <section className="od-roadmap-request" aria-labelledby="roadmap-request-title">
            <div>
              <p className="od-section-label od-section-label-plus">request feature</p>
              <h2 id="roadmap-request-title">What should the agent learn next?</h2>
              <p>Send us the deployment workflow your agent still has to do by hand.</p>
            </div>
            <button
              type="button"
              className="od-roadmap-request-cta"
              onClick={openRequestFeatureModal}
            >
              Request a feature
              <span aria-hidden="true">→</span>
            </button>
          </section>

          <CommunityRequests />
        </main>

        <Footer />
      </div>
      <RequestFeatureModal />
    </>
  )
}
